import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core'
import { ITerritory, User } from '../../models/User'
import { UserService } from '../../services/user.service'
import { ModalComponent } from '../../common/modal/modal.component'
import { TranslateService } from '@ngx-translate/core'
import { AuthenticationService } from '../../services'

declare let navigator: any

@Component({
  standalone: false,
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent implements OnInit {
  @Input() entity: User
  entityCopy: User
  locationResults: any[] = []
  inputValue: string

  showConfirmDelete = false
  territories: string[]

  orderedSelectedLocByTerr: any[] = []
  isEdit = true
  territoriesPlaceholder: ITerritory[]

  modal: ModalComponent
  onDelete: EventEmitter<User> = new EventEmitter<User>()

  @ViewChild('searchInput', { static: true }) searchInput: ElementRef

  errors: any[] = []

  @HostBinding('class.extra-size-1') get isExtraSize1() {
    return this.orderedSelectedLocByTerr.length === 2
  }

  @HostBinding('class.extra-size-2') get isExtraSize2() {
    return (
      (this.orderedSelectedLocByTerr.length === 2 &&
        this.orderedSelectedLocByTerr.filter((terr) => terr.locs.length >= 3)
          .length === 1) ||
      this.orderedSelectedLocByTerr.length === 3
    )
  }

  @HostBinding('class.extra-size-3') get isExtraSize3() {
    return (
      this.orderedSelectedLocByTerr.length >= 2 &&
      this.orderedSelectedLocByTerr.filter((terr) => terr.locs.length >= 3)
        .length >= 2
    )
  }

  @HostBinding('class.extra-size-4') get isExtraSize4() {
    return (
      this.orderedSelectedLocByTerr.length >= 2 &&
      this.orderedSelectedLocByTerr.filter((terr) => terr.locs.length >= 3)
        .length >= 3
    )
  }

  constructor(
    private userService: UserService,
    private translate: TranslateService,
    private auth: AuthenticationService,
  ) {}

  get selectedLocations() {
    return this.entity.territories
  }

  ngOnInit() {
    this.territories = []
    if (!this.entity) {
      this.isEdit = false
      this.entity = new User()
      this.entity.password = this.generatePassword()
    } else {
      this.entityCopy = {
        ...this.entity,
        territories: [...this.entity.territories],
      } as User
      this.isEdit = true
      this.checkChange()
      this.orderSelectedLocByTerr()
    }
  }

  orderSelectedLocByTerr() {
    if (this.selectedLocations.length === 0) {
      this.orderedSelectedLocByTerr = []
    }

    this.orderedSelectedLocByTerr = this.territories
      .map((terr, id) => {
        return {
          name: terr,
          locs: this.selectedLocations.filter((loc) => loc.terrunit - 1 === id),
        }
      })
      .filter((terr) => terr.locs.length !== 0)
  }

  isAlreadySelected(loc) {
    return this.selectedLocations.find((selectedLoc) => {
      return (
        selectedLoc.terrunit_id === loc.terrunit_id &&
        selectedLoc.terrunit === loc.terrunit
      )
    })
  }

  addLocation(loc) {
    if (!this.isAlreadySelected(loc)) {
      this.selectedLocations.push(loc)
      this.orderSelectedLocByTerr()
      this.searchInput.nativeElement.value = ''
      this.locationResults = []
      if (this.isEdit) {
        this.checkChange()
      }
    }
  }

  removeLocation(loc) {
    if (this.isAlreadySelected(loc) && !this.entity.isAdmin) {
      const locIdx = this.selectedLocations.findIndex((selectedLoc) => {
        return (
          selectedLoc.terrunit_id === loc.terrunit_id &&
          selectedLoc.terrunit === loc.terrunit
        )
      })
      if (locIdx !== -1) {
        this.selectedLocations.splice(locIdx, 1)
        this.orderSelectedLocByTerr()
      }
      if (this.isEdit) {
        this.checkChange()
      }
    }
  }

  changeIsAdminState() {
    this.entity.isAdmin = !this.entity.isAdmin
    if (this.isEdit) {
      this.checkChange()
    }
  }

  async searchLocations($event) {
    this.inputValue = $event.target.value
    const results = this.territoriesPlaceholder.filter((terr) => {
      return this.cleanString(terr.name).includes(
        this.cleanString(this.inputValue),
      )
    })
    this.filterFoundLocations(results)
  }

  filterFoundLocations(locations) {
    const filteredResults = locations.filter(
      (terr) => !this.isAlreadySelected(terr),
    )
    this.locationResults = [...filteredResults]
  }

  getTerritory(terrunit) {
    return this.territories[terrunit - 1]
  }

  changeTwoFactorAuthState($event) {
    this.entity.twoFactorAuth = $event
    if (this.isEdit) {
      this.checkChange()
    }
  }

  deleteUser() {
    if (!this.entityCopy.username) {
      return
    }

    if (!this.showConfirmDelete) {
      this.showConfirmDelete = true
    } else {
      this.modal.acceptLoading = true
      this.userService.deleteUser(this.entityCopy).then((_) => {
        this.modal.hide()
        this.modal.acceptLoading = false
        this.onDelete.emit(this.entityCopy)
      })
    }
  }

  backDeleteUser() {
    this.showConfirmDelete = false
  }

  removeSearch() {
    this.inputValue = ''
    this.searchInput.nativeElement.value = ''
  }

  onChangeInput($event, field: string) {
    this.entity[field] = $event.currentTarget.value
    if (this.isEdit) {
      this.checkChange()
    }
  }

  checkChange() {
    let hasChange = false
    Object.keys(this.entityCopy).forEach((key) => {
      if (
        (!Array.isArray(this.entity[key]) &&
          this.entity[key] !== this.entityCopy[key]) ||
        (Array.isArray(this.entity[key]) &&
          this.entity[key].length !== this.entityCopy[key].length)
      ) {
        hasChange = true
      }
    })
    if (!hasChange) {
      this.modal.acceptDisabled = true
      this.modal.cancelButtonText = this.translate.instant('Close')
    } else {
      this.modal.acceptDisabled = false
      this.modal.cancelButtonText = this.translate.instant('Cancel')
    }
  }

  getErrorsByField(field: string) {
    return this.errors
      .filter((err) => err.field === field)
      .map((err) => err.content)
  }

  hasErrors(field: string) {
    return this.getErrorsByField(field).length !== 0
  }

  checkErrors() {
    this.errors = this.entity.validate(this.isEdit)
  }

  copyPassword(ev) {
    navigator.clipboard.writeText(this.entity.password)
    ev.target.classList.add('copied')
    setTimeout(() => {
      ev.target.classList.remove('copied')
    }, 1000)
  }

  private generatePassword() {
    const specialCharacters = '!"#$%&()*+,-./:;<=>?@[]^_`{|}~'
    const randomPassword =
      Math.random().toString(36).slice(-8) +
      specialCharacters[Math.floor(Math.random() * specialCharacters.length)]
    if (!/[\w !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]+/gi.test(randomPassword)) {
      return this.generatePassword()
    } else {
      return randomPassword
    }
  }

  private cleanString(str) {
    const specialChars = '!@#$^&%*()+=-[]/{}|:<>?,.'

    for (let i = 0; i < specialChars.length; i++) {
      str = str.replace(new RegExp('\\' + specialChars[i], 'gi'), '')
    }

    str = str.toLowerCase()

    str = str.replace(/ /g, '_')

    str = str.replace(/á/gi, 'a')
    str = str.replace(/é/gi, 'e')
    str = str.replace(/í/gi, 'i')
    str = str.replace(/ó/gi, 'o')
    str = str.replace(/ú/gi, 'u')
    str = str.replace(/ñ/gi, 'n')
    return str
  }

  isMe() {
    return this.auth.getUser().user.username === this.entityCopy.username
  }
}
